import { useCallback, useMemo, useState } from 'react';
import { Clock, ListOrdered, Loader2, Package, type LucideIcon } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { tutorialFinished, tutorialHeading, tutorialScreens, type PublicTutorialData } from './PublicTutorial';
import { ClipPlayer } from '../ClipPlayer';
import { coverClip, type Clip } from '../assemblyClip';
import { publicMeshUrl } from '../publicUrl';
import { bareMinutes } from '../formatUnits';
import { PublicScreen } from './PublicScreen';

// The model reports the time as printed on the manual, which is often terse
// ("15min"). Spell out a bare minute count; leave any other shape untouched.
function spellOutTime(time: string | null, t: (k: string) => string): string {
  const n = bareMinutes(time);
  return n === null ? (time || '').trim() : `${n} ${t('notice.public.minutes')}`;
}

// THE GROUND THE PRODUCT STANDS ON: cream, lit from above (paler at the top), as in the mockup.
const STAGE = 'bg-[radial-gradient(ellipse_at_50%_15%,#fffdf9_0%,#f4ede2_75%)]';

// First screen a QR-scanner sees: a product card (name + estimated time + step
// count, with an optional hero image) and a single "Ready" button that reveals
// the step videos. Sora for the product name, Satoshi for the rest; brand blue
// (#285EA1) accents on a white page.
export function TutorialSplash({
  docId,
  data,
  brightness,
  variant,
  onVariant,
  onStart,
}: {
  docId: string;
  data: PublicTutorialData;
  // How bright the 3D picture is (see PublicTutorial).
  brightness: number;
  // The colourway picked, held by the page — the player draws its meshes (see PublicTutorial).
  variant: string;
  onVariant: (variant: string) => void;
  onStart: () => void;
}) {
  const { t, language } = useLanguage();
  const heading = tutorialHeading(data, language);

  // The colour/material submodels this product ships in. When there are some, the visitor
  // picks the one they have and the splash shows THAT submodel's photo; the picker only
  // appears when there is a real choice (two or more).
  const submodels = data.submodels ?? [];
  const picked = Math.max(0, submodels.findIndex((s) => s.variant === variant));
  const chosen = submodels[picked];
  // The splash image: the picked submodel's own photo when this product has submodels,
  // else the manual's single hero.
  const splashSrc = submodels.length ? chosen?.photoUrl : data.heroImage;

  const screens = useMemo(() => tutorialScreens(data), [data]);
  // THE PRODUCT ITSELF, IN 3D, in the colourway picked: the player's last screen, turned and
  // zoomed like it (Louis 2026-10-23). The photo stands in where no screen has a clip.
  const finished = useMemo(() => {
    const done = tutorialFinished(screens);
    return done && coverClip(done);
  }, [screens]);
  const meshUrl = useCallback((job: number) => publicMeshUrl(docId, job, variant), [docId, variant]);

  // THE PICKED COLOURWAY LOADS FIRST, the other three once it has appeared (or failed to): all
  // four at once shared the connection and the server's queue, and the one the reader is looking
  // at waited behind the rest (Louis 2026-10-23).
  const [firstIn, setFirstIn] = useState(false);

  // COUNT THE SCREENS THE READER WILL STEP THROUGH, one per sub-step (the player's "01/10"),
  // not the manual's own steps: three of those is ten screens, and "3 steps" undersold it.
  const stepCount = screens.length;
  // Each line with its icon: a clock for the time, a numbered list for the steps.
  const meta: { Icon: LucideIcon; text: string }[] = [];
  const time = spellOutTime(data.time, t);
  if (time) meta.push({ Icon: Clock, text: time });
  if (stepCount > 0) meta.push({ Icon: ListOrdered, text: `${stepCount} ${t('notice.public.steps')}` });

  return (
    <PublicScreen primary={{ label: t('notice.public.ready'), onClick: onStart }}>
      {/* THE WHOLE HEIGHT, the picture taking what the heading leaves: nothing to scroll to
          (Louis 2026-10-23). */}
      <div className="flex h-full flex-col">
        {/* Centred, with the meta underneath it: a long product name then wraps across the
            full width instead of squeezing beside it. */}
        <h1 className="px-10 text-center font-sora text-3xl font-bold leading-tight text-notice-blue">
          {heading}
        </h1>
        {meta.length > 0 && (
          // ON ONE LINE, centred under the heading, the two held apart by space alone (Louis
          // 2026-10-23): two rows on the right were 40px the picture went without.
          <div className="mt-3 flex items-center justify-center gap-6 font-satoshi text-sm font-medium text-notice-blue">
            {meta.map(({ Icon, text }) => (
              <span key={text} className="flex items-center gap-1.5">
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                {text}
              </span>
            ))}
          </div>
        )}

        {/* THE PRODUCT, IN 3D. Where it ships in several colourways, the picture IS the picker:
            each one in its own colours, two by two, pressed to pick it, and no larger picture
            beside them (Louis 2026-10-23). Its name is the tile's label. */}
        {finished && submodels.length >= 2 ? (
          <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <div className="text-center font-satoshi text-xs font-semibold uppercase tracking-wide text-notice-blue/70">
              {t('notice.public.chooseVariant')}
            </div>
            <div className="mt-2 grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-3">
              {submodels.map((s, i) => (
                <VariantThumb
                  load={i === picked || firstIn}
                  onSettled={i === picked ? () => setFirstIn(true) : undefined}
                  key={s.variant}
                  clip={finished}
                  docId={docId}
                  variant={s.variant}
                  label={s.label || s.variant}
                  brightness={brightness}
                  picked={i === picked}
                  onPick={() => onVariant(s.variant)}
                />
              ))}
            </div>
          </div>
        ) : finished ? (
          <ClipPlayer
            clip={finished}
            label={heading}
            meshUrl={meshUrl}
            className={`mt-4 min-h-0 w-full flex-1 rounded-2xl ${STAGE}`}
            emptyClassName="mt-4 min-h-0 w-full flex-1 rounded-2xl text-notice-blue/50"
            brightness={brightness}
            autoPlay={false}
            still
            noBigView
            floorShadow
          />
        ) : splashSrc ? (
          <img
            src={splashSrc}
            alt={chosen ? `${heading} — ${chosen.label || chosen.variant}` : heading}
            className="mt-5 max-h-72 w-full rounded-2xl object-contain"
          />
        ) : (
          // No finished-product picture was found in the manual (Phase 1 detects
          // one and Phase 6 publishes the crop) — stand in for it.
          <div className="mt-5 flex aspect-square w-full items-center justify-center rounded-2xl border border-notice-blue/15 bg-white/50">
            <Package className="h-20 w-20 text-notice-blue/25" strokeWidth={1.25} />
          </div>
        )}

        {/* Named chips, where there are colourways and no 3D to show them in. */}
        {!finished && submodels.length >= 2 && (
          <div className="mt-4">
            <div className="text-center font-satoshi text-xs font-semibold uppercase tracking-wide text-notice-blue/70">
              {t('notice.public.chooseVariant')}
            </div>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {submodels.map((s, i) => (
                <button
                  key={s.variant}
                  type="button"
                  onClick={() => onVariant(s.variant)}
                  aria-pressed={i === picked}
                  className={`rounded-full border px-3 py-1.5 font-satoshi text-sm font-medium transition-colors ${
                    i === picked
                      ? 'border-notice-blue bg-notice-blue text-white'
                      : 'border-notice-blue/30 bg-white/60 text-notice-blue hover:border-notice-blue/60'
                  }`}
                >
                  {s.label || s.variant}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </PublicScreen>
  );
}

// ONE SUBMODEL IN THE PICKER: the product in its colours, a still. Any press on it picks it, a
// drag too, which also turns it round on its floor (see AssemblyView.floorShadow).
function VariantThumb({ clip, docId, variant, label, brightness, picked, onPick, load, onSettled }: {
  // Whether to load its picture yet (see firstIn); a spinner until then.
  load: boolean;
  onSettled?: () => void;
  clip: Clip;
  docId: string;
  variant: string;
  label: string;
  brightness: number;
  picked: boolean;
  onPick: () => void;
}) {
  const meshUrl = useCallback((job: number) => publicMeshUrl(docId, job, variant), [docId, variant]);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={picked}
      aria-label={label}
      onPointerDown={onPick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}
      className={`relative min-h-0 cursor-pointer overflow-hidden rounded-xl border-2 ${STAGE} ${picked ? 'border-notice-blue' : 'border-transparent'}`}
    >
      {load ? (
        <ClipPlayer
          clip={clip}
          label={label}
          meshUrl={meshUrl}
          className="h-full w-full"
          emptyClassName="h-full w-full text-notice-blue/50"
          brightness={brightness}
          autoPlay={false}
          still
          noBigView
          floorShadow
          onSettled={onSettled}
          // THE FOUR TILES ON ONE GPU CONTEXT, so the chair they share is uploaded once and not
          // four times, 184 MB on a phone that was killing the page (Louis 2026-10-26).
          sharedGl="splash-tiles"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin opacity-60" />
        </div>
      )}
    </div>
  );
}
